import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useAccountStore } from '../store';
import { Switch } from '@headlessui/react';
import axios, { apiBaseUrl } from "../services/http";
import { ToastContainer, toast } from "react-toastify";

export default function Settings() {
  const { me, fetch } = useAccountStore();
  const [settings, setSettings] = useState(me.settings);
  const [submit, setSubmit] = useState({
    status: 'idle',
    data: null,
    error: null,
  });

  const onSubmit = async () => {
    setSubmit(state => ({...state, status: 'pending'}));
    try {
      await axios.put(`${apiBaseUrl}/api/account-settings`, settings);
      await fetch();
      toast.success("Settings updated.");
      setSubmit((state) => ({ ...state, status: "succeeded" }));
    } catch (error) {
      setSubmit((state) => ({ ...state, status: "failed", error: error.response.data.message }));
    }
  };

  const shouldUpdate = () => {
    return submit.status !== 'pending' && Object.keys(me.settings).filter(key => me.settings[key] !== settings[key]).length > 0;
  };

  useEffect(() => {
    console.log(me);
  }, [me]);

  return (
    <>
      <div className="grid lg:grid-cols-2 grid-cols-1 lg:divide-x">
        <div className="lg:pr-10">
          <h3 className="text-lg font-bold mb-4">Profile Details</h3>
          <div className="space-y-4">
            <div>
              <p className="font-semibold mb-1">Display name:</p>
              <input
                type="text"
                readonly
                disabled
                className="rounded bg-gray-100 text-gray-500 px-4 py-3 border-none w-full"
                value={me.display_name}
              />
            </div>
            <div>
              <p className="font-semibold mb-1">E-mail:</p>
              <input
                type="text"
                readonly
                disabled
                className="rounded bg-gray-100 text-gray-500 px-4 py-3 border-none w-full"
                value={me.email}
              />
            </div>
            <div>
              <p className="font-semibold mb-1">User group:</p>
              <input
                type="text"
                readonly
                disabled
                className="rounded bg-gray-100 text-gray-500 px-4 py-3 border-none w-full"
                value={me.user_group}
              />
            </div>
            <div>
              <p className="font-semibold mb-1">Maximum active bookings:</p>
              <input
                type="text"
                readonly
                disabled
                className="rounded bg-gray-100 text-gray-500 px-4 py-3 border-none w-full"
                value={me.max_active_bookings}
              />
            </div>
          </div>
        </div>
        <div className="lg:pl-10 lg:mt-0 mt-10">
          <h3 className="text-lg font-bold mb-4">Settings</h3>
          <p className="mb-4">All notifications will be sent to your registered email address.</p>
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="grow">
                <p className="font-semibold">Enable invitations</p>
                <p className="text-gray-500">
                  You can be invited to group bookings.
                </p>
              </div>
              <Switch
                checked={settings.enable_invitation}
                onChange={(val) =>
                  setSettings((state) => ({ ...state, enable_invitation: val }))
                }
                className={`${
                  settings.enable_invitation ? "bg-orange-500" : "bg-gray-200"
                } relative inline-flex h-[26px] w-[52px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
              >
                <span
                  aria-hidden="true"
                  className={`${
                    settings.enable_invitation
                      ? "translate-x-7"
                      : "translate-x-0"
                  } pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
            <div className="flex items-center">
              <div className="grow">
                <p className="font-semibold">Notification on new bookings</p>
                <p className="text-gray-500">
                  You will be notified when you place new bookings.
                </p>
              </div>
              <Switch
                checked={settings.notify_new_booking}
                onChange={(val) =>
                  setSettings((state) => ({
                    ...state,
                    notify_new_booking: val,
                  }))
                }
                className={`${
                  settings.notify_new_booking ? "bg-orange-500" : "bg-gray-200"
                } relative inline-flex h-[26px] w-[52px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
              >
                <span className="sr-only">Use setting</span>
                <span
                  aria-hidden="true"
                  className={`${
                    settings.notify_new_booking
                      ? "translate-x-7"
                      : "translate-x-0"
                  } pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
            <div className="flex items-center">
              <div className="grow">
                <p className="font-semibold">
                  Notification on cancelled bookings
                </p>
                <p className="text-gray-500">
                  You will be notified when you or your group members cancel
                  bookings.
                </p>
              </div>
              <Switch
                checked={settings.notify_cancelled_booking}
                onChange={(val) =>
                  setSettings((state) => ({
                    ...state,
                    notify_cancelled_booking: val,
                  }))
                }
                className={`${
                  settings.notify_cancelled_booking
                    ? "bg-orange-500"
                    : "bg-gray-200"
                } relative inline-flex h-[26px] w-[52px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
              >
                <span
                  aria-hidden="true"
                  className={`${
                    settings.notify_cancelled_booking
                      ? "translate-x-7"
                      : "translate-x-0"
                  } pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
            <div className="flex items-center">
              <div className="grow">
                <p className="font-semibold">Check-in Reminder</p>
                <p className="text-gray-500">
                  You will be notified when your booking session begins.
                </p>
              </div>
              <Switch
                checked={settings.notify_checkin}
                onChange={(val) =>
                  setSettings((state) => ({
                    ...state,
                    notify_checkin: val,
                  }))
                }
                className={`${
                  settings.notify_checkin ? "bg-orange-500" : "bg-gray-200"
                } relative inline-flex h-[26px] w-[52px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
              >
                <span
                  aria-hidden="true"
                  className={`${
                    settings.notify_checkin ? "translate-x-7" : "translate-x-0"
                  } pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
            <button
              type="submit"
              className={clsx(
                "text-white font-semibold bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 rounded-lg px-4 py-2 text-center",
                shouldUpdate() ? "" : "opacity-50 pointer-events-none"
              )}
              disabled={!shouldUpdate()}
              onClick={onSubmit}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-right"
        hideProgressBar={true}
        closeButton={false}
        limit={1}
      />
    </>
  );
}